import { AnimatePresence, motion } from 'framer-motion';
import {
    FaBook,
    FaCalendarAlt,
    FaChevronDown,
    FaChevronUp,
    FaClock,
    FaDollarSign,
    FaEye,
    FaInfoCircle,
    FaMapMarkerAlt,
    FaReceipt,
    FaShoppingCart
} from 'react-icons/fa';
import {
    getReceiptCompatibleOrder,
    getProductImage,
    getCleanProductName,
    getCategoryName,
    getOrderAddOnLineItems,
    isOrderZellePayment,
    getOrderPaymentReference,
    getOrderSpecialItemFlags,
    getProductFromOrder,
    getScheduledProductFromOrder
} from '../utils/orderUtils';
import StatusBadge from './StatusBadge';
import FormatByOperatingLocation from './FormatByOperatingLocation';

const OrderCard = ({
    order,
    index,
    isExpanded,
    onToggleExpand,
    onViewReceipt,
    onDownloadInvoice,
    onCancelBooking,
    formatDate,
    formatBookingDuration,
    getTimezoneAbbreviation,
    getDomainTextColor,
    getDomainBackgroundColor,
    getDomainBorderColor,
    getActionVerb,
    getDocumentTypeDisplay,
    isFlexOfficeDomain,
    isRetailDomain,
    isEducationDomain,
    canBeCancelled,
    companyDetails
}) => {
    const displayOrder = getReceiptCompatibleOrder(order);
    const productImage = getProductImage(displayOrder);
    const productName = getCleanProductName(displayOrder);
    const categoryName = getCategoryName(displayOrder);
    const documentTypeDisplay = getDocumentTypeDisplay();
    const orderStatus = order?.currentStatus || order?.status || 'PAID';
    const isPaymentPending = ['PAYMENT_PENDING', 'PAYMENT_PROCESSING'].includes(orderStatus);
    const isGroupedRecurringOrder = Boolean(order?.isGroupedOrder && order?.childOrders?.length > 0);
    const parentReceiptNumber = order?.parentOrderNumber || '';

    const normalizeMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
    const bookingDuration = formatBookingDuration(displayOrder);
    const subtotal = normalizeMoney(order.totalAmount || order.subtotalAmount || order.totalBeforTax || 0);
    const totalDiscountAmt = normalizeMoney(order.totalDiscountAmt);
    const totalAmtAfterDiscount = normalizeMoney(order.totalAmtAfterDiscount);
    const dueAmount = normalizeMoney(order.dueAmount);
    const derivedDiscount = subtotal > totalAmtAfterDiscount && totalAmtAfterDiscount > 0
        ? normalizeMoney(subtotal - totalAmtAfterDiscount)
        : 0;
    const actualDiscount = derivedDiscount > 0 ? derivedDiscount : totalDiscountAmt;
    const hasDiscount = actualDiscount > 0;
    const totalPaid =
        totalAmtAfterDiscount > 0
            ? totalAmtAfterDiscount
            : hasDiscount
            ? Math.max(0, subtotal - actualDiscount)
            : subtotal;
    const totalDue = totalAmtAfterDiscount > 0 ? totalAmtAfterDiscount : dueAmount > 0 ? dueAmount : totalPaid;
    const summaryLabel = isPaymentPending ? 'Total Due' : 'Total Paid';
    const summaryAmount = isPaymentPending ? totalDue : totalPaid;
    const scheduledProduct = getScheduledProductFromOrder(displayOrder);
    const product = getProductFromOrder(displayOrder);
    const paymentReference = getOrderPaymentReference(order);
    const shouldShowPaymentReference = isOrderZellePayment(order) && Boolean(paymentReference);
    const { hasAddOnItems, hasLateFeeItems } = getOrderSpecialItemFlags(displayOrder);
    const addOnLineItems = getOrderAddOnLineItems(displayOrder);
    const shouldShowSpecialLabels = isFlexOfficeDomain && (hasAddOnItems || hasLateFeeItems);
    const shouldShowReservationSummary = Boolean(bookingDuration && isFlexOfficeDomain && !isRetailDomain && !isEducationDomain);
    const shouldShowCheckoutIcon = isFlexOfficeDomain && hasAddOnItems;
    const addOnSummaryItems = Array.from(
        addOnLineItems.reduce((items, lineItem, index) => {
            const addOnName = lineItem?.product?.name
                || lineItem?.scheduledProduct?.product?.name
                || `Add-on ${index + 1}`;
            const quantity = Number(lineItem?.quantity) || 1;
            const rawAmount = Number(lineItem?.totalAmount);
            const amount = Number.isFinite(rawAmount)
                ? rawAmount
                : normalizeMoney((Number(lineItem?.priceAmt) || Number(lineItem?.rate) || 0) * quantity);
            const itemKey = `${addOnName}::${Number(lineItem?.priceAmt) || Number(lineItem?.rate) || amount}`;
            const existingItem = items.get(itemKey);

            if (existingItem) {
                existingItem.quantity += quantity;
                existingItem.amount = normalizeMoney(existingItem.amount + amount);
                return items;
            }

            items.set(itemKey, {
                name: addOnName,
                quantity,
                amount: normalizeMoney(amount)
            });

            return items;
        }, new Map()).values()
    );

    // ──────────────────────────────────────────────────────────────────────────

    let ratePerHour = 0;
    let hours = 0;
    if (scheduledProduct?.product?.price?.priceAmt) {
        ratePerHour = parseFloat(scheduledProduct.product.price.priceAmt);
    }
    if (scheduledProduct?.period?.duration) {
        hours = parseFloat(scheduledProduct.period.duration);
    }

    const getUserAddresses = () => {
        const addresses = [];
        if (order?.company?.address?.streetName1) {
            addresses.push({
                type: 'company',
                label: companyDetails?.name || 'Company',
                ...order.company.address
            });
        }
        return addresses;
    };

    const getPrimaryAddress = () => {
        if (order?.shippingAddress && (order.shippingAddress.city || order.shippingAddress.streetName1)) {
            return order.shippingAddress;
        }
        if (order?.company?.address) {
            return order.company.address;
        }
        return null;
    };

    const openAddressInMaps = (address) => {
        if (!address) return;
        let addressString = '';
        if (address.streetName1) addressString += address.streetName1;
        if (address.streetName2) addressString += `, ${address.streetName2}`;
        if (address.city) addressString += `, ${address.city}`;
        if (address.state) addressString += `, ${address.state}`;
        if (address.zipcode || address.zipCode) addressString += `, ${address.zipcode || address.zipCode}`;
        if (address.country) addressString += `, ${address.country}`;
        if (addressString) {
            window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressString)}`, '_blank');
        }
    };

    const allAddresses = getUserAddresses();
    const primaryAddress = getPrimaryAddress();

    // Condition for showing location in education domain
    let shouldShowLocation = true;
    if (isEducationDomain) {
        const deliveryType = product?.deliveryType;
        const deliverySubType = product?.deliverySubType;
        if (deliveryType !== 'BRICK_MORTAR' || deliverySubType === 'INSTRUCTOR_LED') {
            shouldShowLocation = false;
        }
    }

    return (
        <motion.div
            id={`order-card-${order.primaryId}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
            className="bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all duration-200"
            style={{ borderColor: isExpanded ? getDomainTextColor() : '' }}
        >
            <div className="p-4 sm:p-5">
                {/* Below sm (640px) the image/name column and the price/actions
                    column were both forced side-by-side into a fixed-width row,
                    squeezing the name column down to a sliver (it could render
                    as a single visible character before truncating). Stacking
                    them below sm and keeping the original side-by-side layout
                    from sm upward (tablet/desktop) fixes that without touching
                    the tablet+ card, which already read fine. */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                        {shouldShowCheckoutIcon ? (
                            <div className="w-16 h-16 flex-shrink-0 rounded-lg border border-slate-200 bg-slate-100 text-slate-500 flex items-center justify-center">
                                <FaShoppingCart className="w-6 h-6" />
                            </div>
                        ) : productImage && (
                            <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                                <img
                                    src={productImage}
                                    alt={productName}
                                    className="w-full h-full object-cover"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            </div>
                        )}

                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-lg font-semibold text-slate-900 truncate">
                                    {productName}
                                </h3>
                                <StatusBadge order={order} />
                            </div>

                            {shouldShowSpecialLabels && (
                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                    {hasAddOnItems && (
                                        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                                            ADD ON
                                        </span>
                                    )}
                                    {hasLateFeeItems && (
                                        <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-rose-700">
                                            LATE FEE
                                        </span>
                                    )}
                                </div>
                            )}

                            {categoryName && (
                                <div className="mb-2">
                                    <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded">
                                        {categoryName}
                                    </span>
                                </div>
                            )}

                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                                {shouldShowReservationSummary ? (
                                    <>
                                        <span className="flex items-center gap-1.5">
                                            <FaCalendarAlt className="w-3.5 h-3.5" />
                                            Reservation: {bookingDuration.dateStr}
                                        </span>
                                        {bookingDuration.hasMeaningfulTime && (
                                            <span className="flex items-center gap-1.5">
                                                <FaClock className="w-3.5 h-3.5" />
                                                {bookingDuration.startTime} - {bookingDuration.endTime} ({getTimezoneAbbreviation()})
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    <span className="flex items-center gap-1.5">
                                        <FaCalendarAlt className="w-3.5 h-3.5" />
                                        {getActionVerb()} on {formatDate(order.createdDate)}
                                    </span>
                                )}
                                {bookingDuration && !isRetailDomain && !isEducationDomain && !shouldShowReservationSummary && (
                                    <span className="flex items-center gap-1.5">
                                        <FaClock className="w-3.5 h-3.5" />
                                        {isEducationDomain ? 'Course Date' : 'Reservation Date'}: {bookingDuration.dateStr}
                                    </span>
                                )}
                            </div>

                            {shouldShowPaymentReference && (
                                <div className="mt-2">
                                    <span className="inline-flex max-w-full items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-xs text-slate-600">
                                        <FaReceipt className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate">Ref ID: {paymentReference}</span>
                                    </span>
                                </div>
                            )}

                            {hasAddOnItems && parentReceiptNumber ? (
                                <p className="mt-2 text-xs text-slate-500">
                                    Checkout order for booking #{parentReceiptNumber}
                                </p>
                            ) : !isGroupedRecurringOrder && parentReceiptNumber ? (
                                <p className="mt-2 text-xs text-slate-500">
                                    Part of recurring booking receipt #{parentReceiptNumber}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-3 flex-shrink-0 ml-0 sm:ml-4 pl-20 sm:pl-0">
                            <div className="text-left sm:text-right">
                                <div className="text-xl font-bold text-slate-900">
                                    <FormatByOperatingLocation value={summaryAmount} type="CURRENCY" />
                                </div>
                                {hasDiscount && (
                                    <p className="text-xs text-violet-600 mt-1">
                                        Discount Applied: <FormatByOperatingLocation value={actualDiscount} type="CURRENCY" />
                                    </p>
                                )}
                            </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => onViewReceipt(order)}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 bg-transparent hover:bg-slate-100"
                                style={{ color: getDomainTextColor() }}
                            >
                                <FaEye className="w-3.5 h-3.5" />
                                View {isEducationDomain ? 'Receipt' : documentTypeDisplay}
                            </button>

                            <button
                                onClick={() => onToggleExpand(order.primaryId)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors flex-shrink-0 bg-transparent hover:bg-slate-100"
                                aria-label={isExpanded ? 'Show less' : 'Show more'}
                                style={{ color: getDomainTextColor() }}
                            >
                                {isExpanded ? <FaChevronUp className="w-4 h-4" /> : <FaChevronDown className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-slate-100"
                    >
                        <div className="p-5" style={{ backgroundColor: getDomainBackgroundColor() }}>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

                                {/* Schedule / Details */}
                                <div className="bg-white rounded-lg p-5 border border-slate-200" style={{ borderColor: getDomainBorderColor() }}>
                                    <h4 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                        {isFlexOfficeDomain ? <FaClock className="w-4 h-4" style={{ color: getDomainTextColor() }} /> :
                                            isRetailDomain ? <FaShoppingCart className="w-4 h-4" style={{ color: getDomainTextColor() }} /> :
                                                isEducationDomain ? <FaBook className="w-4 h-4" style={{ color: getDomainTextColor() }} /> :
                                                    <FaInfoCircle className="w-4 h-4" style={{ color: getDomainTextColor() }} />}
                                        {isFlexOfficeDomain ? 'Schedule' : isRetailDomain ? 'Order Details' : isEducationDomain ? 'Enrollment Details' : 'Details'}
                                    </h4>

                                    {bookingDuration && !isEducationDomain && !isRetailDomain ? (
                                        <div className="space-y-3">
                                            <div className="flex items-start">
                                                <div className="w-32 flex-shrink-0">
                                                    <span className="text-sm text-slate-600">
                                                        {bookingDuration.isGroupedOrder ? 'Date Range:' : 'Reservation Date:'}
                                                    </span>
                                                </div>
                                                <div className="flex-1">
                                                    <span className="text-sm font-medium text-slate-900 flex items-center gap-2">
                                                        <FaCalendarAlt className="w-3.5 h-3.5" style={{ color: getDomainTextColor() }} />
                                                        {bookingDuration.dateStr}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="w-32 flex-shrink-0">
                                                    <span className="text-sm text-slate-600">Time:</span>
                                                </div>
                                                {bookingDuration.hasMeaningfulTime ? (
                                                    <div className="flex-1">
                                                        <span className="text-sm font-medium text-slate-900">
                                                            {bookingDuration.startTime} - {bookingDuration.endTime} ({getTimezoneAbbreviation()})
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="flex-1">
                                                        <span className="text-sm text-slate-500">Not applicable</span>
                                                    </div>
                                                )}
                                            </div>
                                            {bookingDuration.durationStr && (
                                                <div className="flex items-start">
                                                    <div className="w-32 flex-shrink-0">
                                                        <span className="text-sm text-slate-600">
                                                            {bookingDuration.isGroupedOrder ? 'Schedule:' : 'Duration:'}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1">
                                                        <span className="text-sm font-medium text-slate-900">
                                                            {bookingDuration.durationStr}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex items-start">
                                                <div className="w-32 flex-shrink-0">
                                                    <span className="text-sm text-slate-600">{getActionVerb()} On:</span>
                                                </div>
                                                <div className="flex-1">
                                                    <span className="text-sm font-medium text-slate-900">
                                                        {formatDate(order.createdDate)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="flex items-start">
                                                <div className="w-32 flex-shrink-0">
                                                    <span className="text-sm text-slate-600">{getActionVerb()} On:</span>
                                                </div>
                                                <div className="flex-1">
                                                    <span className="text-sm font-medium text-slate-900">
                                                        {formatDate(order.createdDate)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="w-32 flex-shrink-0">
                                                    <span className="text-sm text-slate-600">{isEducationDomain ? 'Receipt' : 'Invoice'} #:</span>
                                                </div>
                                                <div className="flex-1">
                                                    <span className="text-sm font-medium text-slate-900">
                                                        {order.number || order.orderNumber}
                                                    </span>
                                                </div>
                                            </div>
                                            {categoryName && (
                                                <div className="flex items-start">
                                                    <div className="w-32 flex-shrink-0">
                                                        <span className="text-sm text-slate-600">{isEducationDomain ? 'Subject' : 'Category'}:</span>
                                                    </div>
                                                    <div className="flex-1">
                                                        <span className="text-sm font-medium text-slate-900">
                                                            {categoryName}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Payment Summary */}
                                <div className="bg-white rounded-lg p-5 border border-slate-200" style={{ borderColor: getDomainBorderColor() }}>
                                    <div className="flex items-center gap-2.5 mb-4">
                                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                            <FaDollarSign className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-800">Payment Summary</h4>
                                            <p className="text-xs text-slate-400">Breakdown of your charges</p>
                                        </div>
                                    </div>

                                    <div className="border-b border-slate-100 mb-3" />

                                    <div className="space-y-3">
                                        {/* Booking charges row */}
                                        {ratePerHour > 0 && hours > 0 && (
                                            <div className="flex justify-between items-center py-2 px-3 bg-slate-50 rounded-lg border border-slate-100">
                                                <div>
                                                    <p className="text-slate-600 text-xs font-medium">Booking Charges</p>
                                                    <p className="text-slate-400 text-[10px] mt-0.5">
                                                        <FormatByOperatingLocation value={ratePerHour} type="CURRENCY" /> / hour × {hours} hours
                                                    </p>
                                                </div>
                                                <span className="font-semibold text-slate-800 text-xs">
                                                    <FormatByOperatingLocation value={ratePerHour * hours} type="CURRENCY" />
                                                </span>
                                            </div>
                                        )}

                                        {addOnSummaryItems.map((item) => (
                                            <div
                                                key={`${item.name}-${item.quantity}-${item.amount}`}
                                                className="flex justify-between items-center py-2 px-3 bg-amber-50 rounded-lg border border-amber-100"
                                            >
                                                <div>
                                                    <p className="text-amber-700 text-xs font-medium">{item.name}</p>
                                                    <p className="text-amber-600/80 text-[10px] mt-0.5">
                                                        Add-on{item.quantity > 1 ? ` x ${item.quantity}` : ''}
                                                    </p>
                                                </div>
                                                <span className="font-semibold text-amber-700 text-xs">
                                                    <FormatByOperatingLocation value={item.amount} type="CURRENCY" />
                                                </span>
                                            </div>
                                        ))}

                                        {/* Subtotal row (always show so user sees the base) */}
                                        {hasDiscount && (
                                            <div className="flex justify-between items-center py-1 px-1">
                                                <span className="text-slate-500 text-xs">Subtotal</span>
                                                <span className="text-slate-600 text-xs">
                                                    <FormatByOperatingLocation value={subtotal} type="CURRENCY" />
                                                </span>
                                            </div>
                                        )}

                                        {/* Single consolidated discount row —
                                            totalDiscountAmt already combines AMOUNT + PERCENT
                                            from the backend calculation */}
                                        {hasDiscount && (
                                            <div className="flex justify-between items-center py-2 px-3 bg-violet-50 rounded-lg border border-violet-100">
                                                <div>
                                                    <span className="text-violet-600 text-xs font-semibold">
                                                        Discount Applied
                                                    </span>
                                                   
                                                </div>
                                                <span className="font-semibold text-violet-600 text-xs">
                                                    -<FormatByOperatingLocation value={actualDiscount} type="CURRENCY" />
                                                </span>
                                            </div>
                                        )}

                                        <div className="border-t border-dashed border-slate-200 my-2" />

                                        {shouldShowPaymentReference && (
                                            <div className="flex justify-between items-center py-2 px-3 bg-slate-50 rounded-lg border border-slate-100">
                                                <span className="text-slate-500 text-xs font-medium">Reference ID</span>
                                                <span className="max-w-[14rem] truncate font-mono text-xs text-slate-700">
                                                    {paymentReference}
                                                </span>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between px-1">
                                            <div>
                                                <p className="text-slate-800 font-bold text-xs">{summaryLabel}</p>
                                                {hasDiscount && (
                                                    <p className="text-violet-500 text-[10px] font-medium mt-0.5">
                                                        Discount Applied: <FormatByOperatingLocation value={actualDiscount} type="CURRENCY" />
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <span className="text-slate-900 font-extrabold text-xl leading-none">
                                                    <FormatByOperatingLocation value={summaryAmount} type="CURRENCY" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Location - conditionally rendered for education domain */}
                                {shouldShowLocation && (
                                    <div className="bg-white rounded-lg p-5 border border-slate-200" style={{ borderColor: getDomainBorderColor() }}>
                                        <div className="flex items-center justify-between mb-4">
                                            <h4 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                                                <FaMapMarkerAlt className="w-4 h-4" style={{ color: getDomainTextColor() }} />
                                                {allAddresses.length > 0 ? `Location${allAddresses.length > 1 ? 's' : ''}` : 'Location'}
                                            </h4>
                                        </div>

                                        {allAddresses.length > 0 ? (
                                            <div className="space-y-4">
                                                {allAddresses.map((addr, idx) => (
                                                    <AddressCard key={idx} address={addr} onOpenMap={openAddressInMaps} isFirst={idx === 0} />
                                                ))}
                                            </div>
                                        ) : primaryAddress ? (
                                            <AddressCard address={{ label: 'Location', ...primaryAddress }} onOpenMap={openAddressInMaps} isFirst />
                                        ) : (
                                            <div className="text-center py-4">
                                                <FaMapMarkerAlt className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                                <p className="text-sm text-slate-500">No address information available</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            onClick={() => onDownloadInvoice(order)}
                                            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 bg-transparent hover:bg-slate-100"
                                        style={{ color: getDomainTextColor() }}
                                        >
                                            <FaReceipt className="w-3.5 h-3.5" />
                                            Download {isEducationDomain ? 'Receipt' : documentTypeDisplay}
                                        </button>
                                    </div>

                                {canBeCancelled && canBeCancelled(order) && ['PAID', 'PAYMENT_PENDING', 'PAYMENT_PROCESSING'].includes(orderStatus) && (
                                    <button
                                        onClick={() => onCancelBooking(order)}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:text-rose-600 hover:border-rose-300 transition-all duration-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                                    >
                                        <span>Cancel {isEducationDomain ? "Enrollment" : isRetailDomain ? "Order" : "Booking"}</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const AddressCard = ({ address, onOpenMap, isFirst }) => (
    <div className={`p-3 rounded-lg border ${isFirst ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex items-start justify-between mb-2">
            <span className="text-xs font-medium text-slate-700 px-2 py-1 bg-white rounded border border-slate-200">
                {address.label}
            </span>
            <button
                onClick={() => onOpenMap(address)}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
                <FaMapMarkerAlt className="w-3 h-3" />
                View on Map
            </button>
        </div>
        <div className="text-sm space-y-1">
            {address.streetName1 && <p className="text-slate-900 font-medium">{address.streetName1}</p>}
            {address.streetName2 && <p className="text-slate-700">{address.streetName2}</p>}
            {(address.city || address.state || address.zipcode || address.zipCode) && (
                <div className="flex flex-wrap gap-1 mt-1">
                    {address.city && <span className="text-slate-600">{address.city}</span>}
                    {address.state && <span className="text-slate-600">, {address.state}</span>}
                    {(address.zipcode || address.zipCode) && <span className="text-slate-600"> {address.zipcode || address.zipCode}</span>}
                </div>
            )}
            {address.country && <p className="text-slate-600">{address.country}</p>}
        </div>
    </div>
);

export default OrderCard;
